import {
  DEFAULT_TIER_CONFIG,
  RATE_RANGE,
  STREAK_BOOST_OFF,
  type TierConfig,
} from 'Phaser/Gameplay/Complexity/complexityModel.types';

/**
 * The complexity controls the settings panel exposes (MUS-401).
 *
 * These are the user-facing names for engine parameters: Climb Rate is `cai`,
 * Drop Rate is `cdi`, Streak Boost swaps the `aim`/`gim` pair, and Rest Phrases
 * gates and paces rest injection. The panel never names an engine parameter,
 * and the engine never learns a panel label — this module is the only place the
 * two vocabularies meet.
 */
export interface ComplexitySettings {
  /** `cai` — how much complexity climbs on a good phrase. */
  climbRate: number;
  /** `cdi` — how much it drops on a bad one. */
  dropRate: number;
  /** Whether streaks compound the climb. */
  streakBoost: boolean;
  /** Whether rest phrases are injected at all. */
  restPhrasesEnabled: boolean;
  /** Game phrases between rest phrases, when they are enabled. */
  restPhraseCadence: number;
}

/**
 * Defaults, from the design spec rather than from `DEFAULT_TIER_CONFIG`.
 *
 * The rates and Streak Boost match the engine's shipping values, but the rest
 * phrase pair deliberately does not: the engine ships rests on at a cadence of
 * 4, while the panel starts them off at 10. A user who never opens the panel
 * therefore gets no rest phrases, which is the ruled default.
 */
export const DEFAULT_COMPLEXITY_SETTINGS: Readonly<ComplexitySettings> = Object.freeze({
  climbRate: DEFAULT_TIER_CONFIG.cai,
  dropRate: DEFAULT_TIER_CONFIG.cdi,
  streakBoost: true,
  restPhrasesEnabled: false,
  restPhraseCadence: 10,
});

/** Climb and Drop Rate move a percentage point at a time. */
export const RATE_STEP = 0.01;

/** Bounds on the rest phrase counter, per the design spec. */
export const REST_CADENCE_RANGE = Object.freeze({ min: 1, max: 99 });

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/** Rounds to whole percentage points, so repeated stepping cannot drift. */
const roundRate = (rate: number): number => Math.round(rate * 100) / 100;

/** Clamps a Climb/Drop Rate into {@link RATE_RANGE}, on a whole-percent grid. */
export const clampRate = (rate: number): number =>
  roundRate(clamp(rate, RATE_RANGE.min, RATE_RANGE.max));

/** Clamps the rest phrase counter into {@link REST_CADENCE_RANGE}. */
export const clampRestCadence = (cadence: number): number =>
  Math.round(clamp(cadence, REST_CADENCE_RANGE.min, REST_CADENCE_RANGE.max));

/** A rate as the panel displays it: `0.1` reads as `10%`. */
export const formatRate = (rate: number): string => `${Math.round(rate * 100)}%`;

/**
 * Coerces anything shaped like settings into valid settings.
 *
 * Values arrive from `localStorage`, so they can be stale from an older build,
 * hand-edited, or absent. Every field falls back to its default rather than
 * throwing — a corrupt entry should cost the user their preferences, not the
 * lesson.
 */
export function coerceComplexitySettings(value: unknown): ComplexitySettings {
  const raw = (value ?? {}) as Partial<Record<keyof ComplexitySettings, unknown>>;
  const num = (input: unknown, fallback: number): number =>
    typeof input === 'number' && Number.isFinite(input) ? input : fallback;
  const bool = (input: unknown, fallback: boolean): boolean =>
    typeof input === 'boolean' ? input : fallback;

  return {
    climbRate: clampRate(num(raw.climbRate, DEFAULT_COMPLEXITY_SETTINGS.climbRate)),
    dropRate: clampRate(num(raw.dropRate, DEFAULT_COMPLEXITY_SETTINGS.dropRate)),
    streakBoost: bool(raw.streakBoost, DEFAULT_COMPLEXITY_SETTINGS.streakBoost),
    restPhrasesEnabled: bool(
      raw.restPhrasesEnabled,
      DEFAULT_COMPLEXITY_SETTINGS.restPhrasesEnabled,
    ),
    restPhraseCadence: clampRestCadence(
      num(raw.restPhraseCadence, DEFAULT_COMPLEXITY_SETTINGS.restPhraseCadence),
    ),
  };
}

/**
 * The engine patch these settings describe.
 *
 * Streak Boost rides on the `aim`/`gim` pair rather than a flag of its own, so
 * turning it off is a swap to {@link STREAK_BOOST_OFF} rather than a special
 * case in the recurrence.
 */
export function complexitySettingsToTierConfig(settings: ComplexitySettings): Partial<TierConfig> {
  return {
    cai: settings.climbRate,
    cdi: settings.dropRate,
    aim: settings.streakBoost ? DEFAULT_TIER_CONFIG.aim : STREAK_BOOST_OFF.aim,
    gim: settings.streakBoost ? DEFAULT_TIER_CONFIG.gim : STREAK_BOOST_OFF.gim,
    restEnabled: settings.restPhrasesEnabled,
    restCadence: settings.restPhraseCadence,
  };
}
